import { TILE, WALKER_SPEED } from './constants';
import type { PowerupType } from './powerups';
import type { TileType, Vec2 } from './types';

const POWERUP_TYPES: PowerupType[] = ['doublejump', 'armor', 'jump', 'dash', 'run'];

/** On-disk level shape (levels/*.json). Coordinates are TILE INDICES. */
export interface RawLevel {
  id: string;
  name: string;
  tileSize: number;
  width: number;
  height: number;
  spawn: { x: number; y: number };
  goal: { x: number; y: number };
  tiles: string[];
  enemies?: { type: string; x: number; y: number; speed?: number }[];
  coins?: { x: number; y: number }[];
  diamonds?: { x: number; y: number }[];
  powerups?: { type: string; x: number; y: number }[];
}

export interface EnemySpawn {
  type: 'walker';
  /** world-unit top-left spawn position */
  x: number;
  y: number;
  /** patrol speed (units/s); defaults to WALKER_SPEED */
  speed: number;
}

/** Parsed, validated level. Tile coords are tile indices; spawn/goal/enemy
 *  positions are converted to WORLD UNITS. */
export interface Level {
  id: string;
  name: string;
  width: number;
  height: number;
  tiles: TileType[][]; // [y][x]
  spawn: Vec2;
  goal: Vec2;
  enemies: EnemySpawn[];
  coins: Vec2[];
  diamonds: Vec2[];
  powerups: PowerupSpawn[];
}

export interface PowerupSpawn {
  type: PowerupType;
  /** world-unit top-left position */
  x: number;
  y: number;
}

const GLYPHS: Record<string, TileType> = {
  '.': 'empty',
  '#': 'solid',
  '^': 'spike',
  S: 'empty', // spawn marker — `spawn` field is authoritative
  G: 'empty', // goal marker — `goal` field is authoritative
};

/** Parse + validate a raw level. Fails loud on any malformation (Rule 13). */
export function parseLevel(raw: RawLevel): Level {
  if (raw.tileSize !== TILE) {
    throw new Error(`level "${raw.id}": tileSize ${raw.tileSize} must equal engine TILE ${TILE}`);
  }
  if (raw.tiles.length !== raw.height) {
    throw new Error(
      `level "${raw.id}": tiles has ${raw.tiles.length} rows, expected height ${raw.height}`,
    );
  }

  const tiles: TileType[][] = raw.tiles.map((row, y) => {
    if (row.length !== raw.width) {
      throw new Error(
        `level "${raw.id}": row ${y} has length ${row.length}, expected width ${raw.width}`,
      );
    }
    return [...row].map((ch, x) => {
      const t = GLYPHS[ch];
      if (t === undefined) {
        throw new Error(`level "${raw.id}": unknown tile glyph "${ch}" at (${x}, ${y})`);
      }
      return t;
    });
  });

  const enemies: EnemySpawn[] = (raw.enemies ?? []).map((e) => {
    if (e.type !== 'walker') {
      throw new Error(`level "${raw.id}": unknown enemy type "${e.type}"`);
    }
    return { type: 'walker', x: e.x * TILE, y: e.y * TILE, speed: e.speed ?? WALKER_SPEED };
  });

  return {
    id: raw.id,
    name: raw.name,
    width: raw.width,
    height: raw.height,
    tiles,
    spawn: { x: raw.spawn.x * TILE, y: raw.spawn.y * TILE },
    goal: { x: raw.goal.x * TILE, y: raw.goal.y * TILE },
    enemies,
    coins: (raw.coins ?? []).map((c) => ({ x: c.x * TILE, y: c.y * TILE })),
    diamonds: (raw.diamonds ?? []).map((d) => ({ x: d.x * TILE, y: d.y * TILE })),
    powerups: (raw.powerups ?? []).map((p) => {
      if (!POWERUP_TYPES.includes(p.type as PowerupType)) {
        throw new Error(`level "${raw.id}": unknown powerup type "${p.type}"`);
      }
      return { type: p.type as PowerupType, x: p.x * TILE, y: p.y * TILE };
    }),
  };
}

/** Is the tile at (tx, ty) solid? Out-of-bounds is treated as open air
 *  (so the player can fall into a pit and off the sides). */
export function isSolid(level: Level, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= level.width || ty >= level.height) return false;
  return level.tiles[ty][tx] === 'solid';
}

/** Is the tile at (tx, ty) a spike (hazard)? */
export function isSpike(level: Level, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= level.width || ty >= level.height) return false;
  return level.tiles[ty][tx] === 'spike';
}
